import { keyboardModeAttribute, pseudoFocusAttribute } from "../constants/attributes.js";
import { DOWN, TAB, UP } from "../constants/keys.js";
import Chain from "./Chain.js";
export const isKeyboardMode = () => document.documentElement.hasAttribute(keyboardModeAttribute);
export const focusableSelector = 'a,button,input:not([type="hidden"]),textarea,select,details,[tabindex]:not([tabindex="-1"])';
export const getActiveElement = () => {
    const pseudoFocusedEl = document.querySelector(`[${pseudoFocusAttribute}]`);
    return (pseudoFocusedEl || document.activeElement);
};
const getFocusableElements = (rootEl, extraElement) => {
    const focusableElements = Array.from(rootEl.querySelectorAll(focusableSelector));
    const filteredElements = focusableElements.filter((el) => {
        if (el.hasAttribute("disabled"))
            return false;
        if (el.clientHeight === 0)
            return false;
        if (el.type === "radio") {
            let sameNameRadioEls;
            if (el.form) {
                const formInputs = el.form.elements.namedItem(el.name);
                // Synthetic error handling for narrowing down types
                // Radio element can't find itself in the form, so we don't need to include it in the array
                if (!formInputs)
                    return false;
                const multipleElementsFound = "length" in formInputs;
                if (!multipleElementsFound) {
                    // Single element found is always an input radio since we're inside the condition
                    sameNameRadioEls = [formInputs];
                }
                else {
                    sameNameRadioEls = Array.from(formInputs).filter((el) => "type" in el && el.type === "radio");
                }
            }
            else {
                sameNameRadioEls = Array.from(rootEl.querySelectorAll(`[type="radio"][name="${el.name}"]`));
            }
            if (sameNameRadioEls?.length) {
                const checkedEl = Array.from(sameNameRadioEls).find((el) => el.checked);
                if (checkedEl && el !== checkedEl)
                    return false;
                if (!checkedEl && el !== sameNameRadioEls[0])
                    return false;
            }
        }
        return true;
    });
    if (extraElement && filteredElements.length)
        filteredElements.unshift(extraElement);
    return filteredElements;
};
const getFocusData = (args) => {
    const { root, extraElement, target, mode } = args;
    const focusable = getFocusableElements(root, extraElement);
    const focusableLimit = focusable.length - 1;
    const currentElement = getActiveElement();
    const currentIndex = focusable.indexOf(currentElement);
    const positions = {
        next: currentIndex + 1,
        prev: currentIndex - 1,
        first: 0,
        last: focusableLimit,
    };
    let nextIndex = positions[target];
    const isOverflow = nextIndex > focusableLimit || nextIndex < 0;
    if (isOverflow) {
        // Disable circular navigation for action menus
        if (mode === "action-menu") {
            nextIndex = target === "prev" ? positions.first : positions.last;
        }
        else {
            nextIndex = target === "prev" ? positions.last : positions.first;
        }
    }
    return { overflow: isOverflow, el: focusable[nextIndex] };
};
const focusElement = (el, mode) => {
    document.querySelector(`[${pseudoFocusAttribute}]`)?.removeAttribute(pseudoFocusAttribute);
    if (mode === "selection-menu") {
        el.setAttribute(pseudoFocusAttribute, "true");
    }
    else {
        el.focus();
    }
};
const focusTargetElement = (root, target) => {
    const data = getFocusData({ root, target });
    focusElement(data.el);
};
export const focusNextElement = (root) => focusTargetElement(root, "next");
export const focusPreviousElement = (root) => focusTargetElement(root, "prev");
export const focusFirstElement = (root) => focusTargetElement(root, "first");
export const focusLastElement = (root) => focusTargetElement(root, "last");
const trapScreenReader = (() => {
    let affectedElements = [];
    const applyHiddenToSiblings = (el) => {
        let sibling = el && el.parentNode && el.parentNode.firstChild;
        while (sibling) {
            const notCurrent = sibling !== el;
            const isValid = sibling.nodeType === 1 && !sibling.hasAttribute("aria-hidden");
            if (notCurrent && isValid) {
                sibling.setAttribute("aria-hidden", "true");
                affectedElements.push(sibling);
            }
            sibling = sibling.nextSibling;
        }
    };
    const release = () => {
        affectedElements.forEach((el) => {
            el.removeAttribute("aria-hidden");
        });
        affectedElements = [];
    };
    return (el) => {
        let currentEl = el;
        if (affectedElements.length)
            release();
        while (currentEl && currentEl !== document.body) {
            applyHiddenToSiblings(currentEl);
            currentEl = currentEl.parentElement;
        }
        return { release };
    };
})();
export const trapFocus = (() => {
    let resetListeners = null;
    let srTrap = null;
    const chain = new Chain();
    return (root, options = {}) => {
        const { mode = "dialog", onNavigateOutside, includeTrigger } = options;
        const triggerElement = getActiveElement();
        const isDialog = mode === "dialog";
        const isContentMenu = mode === "content-menu";
        const isSelectionMenu = mode === "selection-menu";
        const isTabMode = isDialog || isContentMenu;
        const isArrowsMode = mode === "action-menu" || isSelectionMenu;
        const focusable = getFocusableElements(root, includeTrigger ? triggerElement : undefined);
        let chainId;
        // Re-focus on the first element if content has changed
        const observer = new MutationObserver(() => {
            // Focus stayed inside the wrapper, no need to refocus
            if (root.contains(document.activeElement))
                return;
            const focusable = getFocusableElements(root, includeTrigger ? triggerElement : undefined);
            if (!focusable.length)
                return;
            focusElement(focusable[0], mode);
        });
        const release = (releaseOptions = {}) => {
            const { withoutFocusReturn } = releaseOptions;
            chain.removePreviousTill(chainId, (item) => document.body.contains(item.data.trigger));
            observer.disconnect();
            if (triggerElement) {
                triggerElement.focus({ preventScroll: withoutFocusReturn || !isKeyboardMode() });
            }
            if (resetListeners) {
                resetListeners();
                if (srTrap)
                    srTrap.release();
                resetListeners = null;
                srTrap = null;
            }
            const previousItem = chain.tailId && chain.get(chain.tailId);
            if (previousItem) {
                trapFocus(previousItem.data.root, previousItem.data.options);
            }
        };
        const handleKeyDown = (event) => {
            const key = event.key;
            const isTab = key === TAB;
            const isNextTab = isTab && !event.shiftKey;
            const isBackTab = isTab && event.shiftKey;
            const isUp = isArrowsMode && key === UP;
            const isDown = isArrowsMode && key === DOWN;
            const isPrev = (isBackTab && isTabMode) || isUp;
            const isNext = (isNextTab && isTabMode) || isDown;
            const isFocusedOnTrigger = getActiveElement() === triggerElement;
            const focusData = getFocusData({
                root,
                target: isPrev ? "prev" : "next",
                extraElement: includeTrigger ? triggerElement : undefined,
                mode,
            });
            // Release the trap when tab is used in navigation modes that support arrows
            const hasNavigatedOutside = (isTab && isArrowsMode) || (isContentMenu && isTab && focusData.overflow);
            if (hasNavigatedOutside) {
                // Prevent shift + tab event to avoid focus moving after the trap release
                if (isBackTab && !isFocusedOnTrigger)
                    event.preventDefault();
                release();
                onNavigateOutside?.();
                return;
            }
            // We return after the last condition because Tab can be used for releasing in arrows mode
            if (!isPrev && !isNext)
                return;
            event.preventDefault();
            if (!focusData.el)
                return;
            focusElement(focusData.el, mode);
        };
        // Reset event listeners if focus is trapped elsewhere
        if (resetListeners)
            resetListeners();
        if (isDialog)
            srTrap = trapScreenReader(root);
        observer.observe(root, { childList: true, subtree: true });
        if (!focusable.length)
            return null;
        document.addEventListener("keydown", handleKeyDown);
        resetListeners = () => document.removeEventListener("keydown", handleKeyDown);
        // Don't add back to the chain if we're traversing back
        const tailItem = chain.tailId && chain.get(chain.tailId);
        if (!tailItem || root !== tailItem.data.root) {
            chainId = chain.add({ root, trigger: triggerElement, options });
            focusElement(focusable[0], mode);
        }
        return release;
    };
})();
